import type { AccessRole } from "../types/access.types";

/**
 * Read roles withheld from the default pick, mirroring `SENSITIVE_MODULES` in coloca-nest's
 * `member-default-scopes.constant.ts`. They expose card PANs and transfer detail, and exist as separate
 * modules precisely so they are granted deliberately — a default must not hand them out.
 */
const SENSITIVE_READ_SLUGS = new Set([
  "cards_sensitive_read",
  "cards_topup_sensitive_read",
  "wallets_transfer_sensitive_read",
]);

/**
 * The roles an invitation starts with ticked: read on every non-sensitive module.
 *
 * This is the read-only default made explicit. It is **not** `full_read_admin_access`, even though that
 * aggregate says the same thing in one row: it is one of the two global `is_default` platform roles, and
 * `RolesService.resolveAssignableRoles` refuses those for an organization-scoped caller, so pre-ticking it
 * would make every invitation sent by an org admin fail with `ROLE_NOT_VISIBLE`. The per-resource base roles
 * carry the same effective access and an org admin may assign them.
 *
 * Derived from the catalog rather than a hardcoded list, so a new backend module flows in the moment its base
 * roles are seeded — the same property `MEMBER_DEFAULT_SCOPES` has on the server.
 */
export function defaultReadOnlyRoleIds(roles: AccessRole[]): string[] {
  return roles
    .filter(
      (role) =>
        // Global catalog only: an org's custom roles are its own business, not a default.
        role.organization_id == null &&
        // Excludes the two aggregates (`full_admin_access`, `full_read_admin_access`).
        !role.is_default &&
        role.slug.endsWith("_read") &&
        !SENSITIVE_READ_SLUGS.has(role.slug),
    )
    .map((role) => role.id);
}
